// TutorialEditPage - Página de edição de tutorial com TipTap
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTutorial } from "@/hooks/useTutorials";
import { useUpdateTutorial, useCreateTutorial } from "@/hooks/useTutorials";
import TutorialEditor from '../../components/TutorialEditor/TutorialEditor';
import AdminLayout from '@/layouts/AdminLayout/AdminLayout';
import toast from 'react-hot-toast';

/** Converte texto de input numérico em número ou null, nunca NaN
 *  (campo vazio ou intermediário como "-" viraria NaN e quebrava o save). */
function parseNullableInt(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

const TutorialEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'novo';

  // Para edição, buscar por ID; para novo, não buscar
  const { data: tutorialData, isLoading } = !isNew && id
    ? useTutorial(id)
    : { data: null, isLoading: false };
  const updateMutation = useUpdateTutorial();
  const createMutation = useCreateTutorial();

   const [title, setTitle] = useState('');
   const [description, setDescription] = useState('');
   const [content, setContent] = useState('');
   const [categoryId, setCategoryId] = useState<number | null>(null);
   const [isPublished, setIsPublished] = useState(false);
   const [estimatedDuration, setEstimatedDuration] = useState<number | null>(null);
   const [isFree, setIsFree] = useState(true);
   const [thumbnailUrl, setThumbnailUrl] = useState('');
 
   useEffect(() => {
     const tutorial = (tutorialData as any)?.data;
     if (tutorial) {
       setTitle(tutorial.Title || tutorial.title || '');
       setDescription(tutorial.Description || tutorial.description || '');
       setContent(tutorial.Content || tutorial.content || '');
       setCategoryId(tutorial.CategoryId ?? tutorial.categoryId ?? null);
       setIsPublished(tutorial.IsPublished || tutorial.isPublished || false);
       setEstimatedDuration(tutorial.EstimatedDuration ?? tutorial.estimatedDuration ?? null);
       setIsFree(tutorial.IsFree !== undefined ? tutorial.IsFree : (tutorial.isFree !== undefined ? tutorial.isFree : true));
       setThumbnailUrl(tutorial.ThumbnailUrl || tutorial.thumbnailUrl || '');
     }
   }, [tutorialData]);
 
   const handleSave = async () => {
     if (!title || !content) {
       toast.error('Título e conteúdo são obrigatórios');
       return;
     }
 
     try {
       const payload = {
         title,
         description,
         content,
         categoryId,
         isPublished,
         estimatedDuration,
         isFree,
         thumbnailUrl
       };
 
       if (isNew) {
         await createMutation.mutateAsync(payload);
         toast.success('Tutorial criado com sucesso!');
         navigate('/admin/tutoriais');
       } else {
         if (id) {
           await updateMutation.mutateAsync({ id, data: payload });
           toast.success('Tutorial atualizado com sucesso!');
         }
       }
     } catch (error) {
       toast.error('Erro ao salvar tutorial');
     }
   };
 
   if (!isNew && isLoading) {
     return <div>Carregando...</div>;
   }

   const sections = [
     { id: 'dashboard', label: 'Dashboard' },
     { id: 'tutorials', label: 'Tutoriais' },
     { id: 'categories', label: 'Categorias' },
     { id: 'header-menus', label: 'Menus do Header' },
     { id: 'media', label: 'Mídia' },
     { id: 'knowledge-base', label: 'Base de Conhecimento' },
   ];

   return (
    <AdminLayout sections={sections} activeSection="tutorials" onSelectSection={() => navigate('/admin')}>
     <div className="tutorial-edit-page max-w-5xl mx-auto p-8 bg-white rounded-3xl shadow-sm border border-slate-100 my-10">
       <div className="edit-header flex items-center justify-between mb-10 pb-6 border-b border-slate-50">
         <div>
            <h1 className="text-3xl font-black text-slate-900 tracking-tight">{isNew ? 'Criar Novo Tutorial' : 'Configurar Tutorial'}</h1>
            <p className="text-slate-500 font-medium">Configure os detalhes e conteúdos da sua aula.</p>
         </div>
         <button onClick={handleSave} className="px-8 h-12 bg-black text-white rounded-2xl font-bold hover:scale-105 active:scale-95 transition-all">
           Salvar Alterações
         </button>
       </div>
 
       <div className="edit-form space-y-8">
         <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="form-group space-y-2">
               <label className="text-xs font-black uppercase tracking-widest text-slate-400">Título da Aula</label>
               <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ex: Introdução ao Sistema"
                  className="w-full h-12 px-4 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:border-slate-200 outline-none transition-all font-bold"
               />
            </div>

            <div className="form-group space-y-2">
               <label className="text-xs font-black uppercase tracking-widest text-slate-400">ID da Categoria (Módulo)</label>
               <input
                  type="number"
                  value={categoryId === null ? '' : categoryId}
                  onChange={(e) => setCategoryId(parseNullableInt(e.target.value))}
                  placeholder="ID do Módulo"
                  className="w-full h-12 px-4 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:border-slate-200 outline-none transition-all"
               />
            </div>
         </div>

         <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="form-group space-y-2">
               <label className="text-xs font-black uppercase tracking-widest text-slate-400">Duração estimada (minutos)</label>
               <input
                  type="number"
                  min={0}
                  value={estimatedDuration === null ? '' : estimatedDuration}
                  onChange={(e) => setEstimatedDuration(parseNullableInt(e.target.value))}
                  placeholder="Ex: 5"
                  className="w-full h-12 px-4 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:border-slate-200 outline-none transition-all"
               />
            </div>

            <div className="form-group space-y-2">
               <label className="text-xs font-black uppercase tracking-widest text-slate-400">URL da Thumbnail</label>
               <input
                  type="text"
                  value={thumbnailUrl}
                  onChange={(e) => setThumbnailUrl(e.target.value)}
                  placeholder="https://..."
                  className="w-full h-12 px-4 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:border-slate-200 outline-none transition-all font-mono text-xs"
               />
            </div>

            <div className="form-group flex flex-col justify-center">
               <label className="flex items-center gap-3 cursor-pointer group">
                  <div className={`w-12 h-6 rounded-full transition-all relative ${isFree ? 'bg-emerald-500' : 'bg-slate-200'}`}>
                     <input
                        type="checkbox"
                        checked={isFree}
                        onChange={(e) => setIsFree(e.target.checked)}
                        className="sr-only"
                     />
                     <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${isFree ? 'left-7' : 'left-1'}`} />
                  </div>
                  <span className="text-sm font-bold text-slate-600">Disponível Grátis?</span>
               </label>
            </div>
         </div>
 
         <div className="form-group space-y-2">
           <label className="text-xs font-black uppercase tracking-widest text-slate-400">Descrição Curta (Intro)</label>
           <textarea
             value={description}
             onChange={(e) => setDescription(e.target.value)}
             placeholder="Um resumo editorial para atrair o aluno..."
             rows={3}
             className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:border-slate-200 outline-none transition-all"
           />
         </div>
 
         <div className="form-group space-y-2">
           <label className="text-xs font-black uppercase tracking-widest text-slate-400">Conteúdo do Tutorial (Resumo)</label>
           <div className="border border-slate-100 rounded-[1.5rem] overflow-hidden">
             <TutorialEditor
               content={content}
               onChange={setContent}
               onSave={handleSave}
             />
           </div>
         </div>
 
         <div className="form-actions pt-6 border-t border-slate-50">
           <label className="flex items-center gap-3 cursor-pointer">
             <input
               type="checkbox"
               checked={isPublished}
               onChange={(e) => setIsPublished(e.target.checked)}
               className="w-5 h-5 rounded border-slate-300 text-black focus:ring-black"
             />
             <span className="text-sm font-bold text-slate-900">Publicar Tutorial Imediatamente</span>
           </label>
         </div>
       </div>
     </div>
    </AdminLayout>
  );
};

export default TutorialEditPage;
